import { useState, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'
import { BusinessReportService } from '@/api/generated/services/BusinessReportService'
import type { ReportTemplateFieldSettings } from '@/api/generated/models/ReportTemplateFieldSettings'

interface ReportFieldSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  /** 저장(프롬프트 업데이트) 성공 후 호출 — 부모가 최신 report_settings 를 다시 받아오도록 */
  onSaved?: () => void
  businessId: string
  businessName?: string
}

type PromptMap = Record<string, Record<string, string>>

/** GET/PUT 응답(템플릿 머지 뷰) → 편집용 prompt 맵 */
const buildPromptMap = (templates: ReportTemplateFieldSettings[]): PromptMap => {
  const map: PromptMap = {}
  for (const tpl of templates) {
    map[tpl.template_key] = {}
    for (const field of tpl.fields ?? []) {
      map[tpl.template_key][field.key] = (field.settings?.prompt as string) ?? ''
    }
  }
  return map
}

export default function ReportFieldSettingsModal({
  isOpen,
  onClose,
  onSaved,
  businessId,
  businessName,
}: ReportFieldSettingsModalProps) {
  const { showSuccess, showError, showWarning } = useAlert()

  const [templates, setTemplates] = useState<ReportTemplateFieldSettings[]>([])
  const [prompts, setPrompts] = useState<PromptMap>({})
  const [initialPrompts, setInitialPrompts] = useState<PromptMap>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // 좌측에서 선택한 보고서 템플릿 (마스터-디테일)
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null)

  // 모달 열릴 때 설정 폼 로드
  useEffect(() => {
    if (!isOpen || !businessId) return
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const response = await BusinessReportService.appsBusinessViewsGetReportFieldSettings({ businessId })
        if (cancelled) return
        const list = response ?? []
        setTemplates(list)
        setSelectedTemplateKey(list[0]?.template_key ?? null)
        const map = buildPromptMap(list)
        setPrompts(map)
        setInitialPrompts(map)
      } catch (error) {
        if (!cancelled) handleApiError(error, showError, ErrorMessages.FETCH_FAILED)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [isOpen, businessId, showError])

  const setPrompt = useCallback((templateKey: string, fieldKey: string, value: string) => {
    setPrompts(prev => ({
      ...prev,
      [templateKey]: { ...prev[templateKey], [fieldKey]: value },
    }))
  }, [])

  const handleClose = () => {
    setTemplates([])
    setPrompts({})
    setInitialPrompts({})
    setSelectedTemplateKey(null)
    onClose()
  }

  const handleSave = async () => {
    // 변경된 (template, field) 만 병합 전송
    const fieldSettings: Record<string, Record<string, Record<string, any>>> = {}
    for (const tpl of templates) {
      for (const field of tpl.fields ?? []) {
        const cur = prompts[tpl.template_key]?.[field.key] ?? ''
        const init = initialPrompts[tpl.template_key]?.[field.key] ?? ''
        if (cur !== init) {
          if (!fieldSettings[tpl.template_key]) fieldSettings[tpl.template_key] = {}
          fieldSettings[tpl.template_key][field.key] = { prompt: cur }
        }
      }
    }

    if (Object.keys(fieldSettings).length === 0) {
      showWarning('변경된 내용이 없습니다.')
      return
    }

    setIsSaving(true)
    try {
      const response = await BusinessReportService.appsBusinessViewsUpdateReportFieldSettings({
        businessId,
        requestBody: { field_settings: fieldSettings },
      })
      // 갱신된 머지 뷰로 동기화
      const updated = response.templates ?? []
      if (updated.length > 0) {
        setTemplates(updated)
        const map = buildPromptMap(updated)
        setPrompts(map)
        setInitialPrompts(map)
      } else {
        setInitialPrompts(prompts)
      }
      if (response.dropped && response.dropped.length > 0) {
        showWarning(`일부 항목은 저장되지 않았습니다: ${response.dropped.join(', ')}`)
      }
      showSuccess('시스템 프롬프팅 설정이 저장되었습니다.')
      onSaved?.()
      onClose()
    } catch (error) {
      handleApiError(error, showError, ErrorMessages.UPDATE_FAILED)
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded w-[760px] max-w-[90vw] max-h-[90vh] overflow-auto border flex flex-col"
        style={{ borderColor: designTokens.colors.gray[1] }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2
              className="font-bold"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6],
              }}
            >
              시스템 프롬프팅 설정{businessName ? ` — ${businessName}` : ''}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Content — 필드 수와 무관하게 일정한 높이 유지 (내부만 스크롤) */}
          <div style={{ height: '56vh' }} className="flex flex-col">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <span style={{ color: designTokens.colors.gray[4], fontFamily: designTokens.typography.fontFamily.sans[0] }}>
                불러오는 중...
              </span>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <span style={{ color: designTokens.colors.gray[4], fontFamily: designTokens.typography.fontFamily.sans[0] }}>
                설정할 보고서가 없습니다.
              </span>
            </div>
          ) : (
            <div className="flex gap-4 h-full min-h-0">
              {/* 좌: 보고서 템플릿 목록 */}
              <div
                className="w-[200px] flex-shrink-0 flex flex-col gap-1 overflow-y-auto pr-2 border-r h-full"
                style={{ borderRightColor: designTokens.colors.gray[1] }}
              >
                {templates.map((tpl) => {
                  const isActive = tpl.template_key === selectedTemplateKey
                  const fieldCount = tpl.fields?.length ?? 0
                  return (
                    <button
                      key={tpl.template_key}
                      type="button"
                      onClick={() => setSelectedTemplateKey(tpl.template_key)}
                      className="text-left rounded px-3 py-2 transition-colors"
                      style={{
                        backgroundColor: isActive ? designTokens.colors.primary.DEFAULT : 'transparent',
                        color: isActive ? designTokens.colors.white : designTokens.colors.gray[6],
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      <span className="block truncate">{tpl.name}</span>
                      <span
                        className="block"
                        style={{ fontSize: '11px', color: isActive ? designTokens.colors.gray[1] : designTokens.colors.gray[4] }}
                      >
                        필드 {fieldCount}개
                      </span>
                    </button>
                  )
                })}
              </div>

              {/* 우: 선택한 템플릿의 필드 */}
              <div className="flex-1 min-w-0 overflow-y-auto pr-1 h-full">
                {(() => {
                  const tpl = templates.find(t => t.template_key === selectedTemplateKey)
                  if (!tpl) return null
                  const fields = tpl.fields ?? []
                  return (
                    <div className="flex flex-col gap-5">
                      {tpl.description && (
                        <p style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '13px', color: designTokens.colors.gray[5], margin: 0 }}>
                          {tpl.description}
                        </p>
                      )}
                      {fields.length === 0 ? (
                        <span style={{ fontSize: '13px', color: designTokens.colors.gray[4], fontFamily: designTokens.typography.fontFamily.sans[0] }}>
                          필드가 없습니다.
                        </span>
                      ) : (
                        fields.map((field) => {
                          const value = prompts[tpl.template_key]?.[field.key] ?? ''
                          return (
                            <div key={field.key} className="flex flex-col gap-1.5">
                              <label
                                style={{
                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                                  fontWeight: 600,
                                  color: designTokens.colors.gray[6],
                                }}
                              >
                                {field.name}
                                {field.required && (
                                  <span className="ml-1" style={{ fontSize: '12px', color: designTokens.colors.error.DEFAULT }}>*</span>
                                )}
                                <span className="ml-2" style={{ fontSize: '12px', fontWeight: 400, color: designTokens.colors.gray[4] }}>
                                  ({field.key})
                                </span>
                              </label>
                              {field.description && (
                                <span style={{ fontSize: '12px', color: designTokens.colors.gray[4], fontFamily: designTokens.typography.fontFamily.sans[0] }}>
                                  {field.description}
                                </span>
                              )}

                              <textarea
                                value={value}
                                onChange={(e) => setPrompt(tpl.template_key, field.key, e.target.value)}
                                placeholder="기본 프롬프트에 더해 보낼 내용을 입력해주세요. (선택)"
                                rows={3}
                                className="w-full rounded border px-3 py-2 outline-none resize-y"
                                style={{
                                  borderColor: designTokens.colors.gray[2],
                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                                  lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                                  color: designTokens.colors.gray[6],
                                }}
                              />

                              {/* 기본 프롬프트 — 접기 (항상 AI 요청에 포함, 읽기 전용) */}
                              {field.default_prompt && (
                                <details className="rounded" style={{ backgroundColor: designTokens.colors.gray[0], border: `1px solid ${designTokens.colors.gray[1]}`, padding: '6px 10px' }}>
                                  <summary
                                    style={{
                                      cursor: 'pointer',
                                      fontFamily: designTokens.typography.fontFamily.sans[0],
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      color: designTokens.colors.gray[5],
                                    }}
                                  >
                                    기본 프롬프트 보기 (항상 포함)
                                  </summary>
                                  <p
                                    style={{
                                      fontFamily: designTokens.typography.fontFamily.sans[0],
                                      fontSize: '13px',
                                      color: designTokens.colors.gray[6],
                                      whiteSpace: 'pre-wrap',
                                      margin: '6px 0 0',
                                    }}
                                  >
                                    {field.default_prompt}
                                  </p>
                                </details>
                              )}
                            </div>
                          )
                        })
                      )}
                    </div>
                  )
                })()}
              </div>
            </div>
          )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-[10px]">
            <Button color="light" size="medium" onClick={handleClose} disabled={isSaving} className="px-[10px] h-10">
              취소
            </Button>
            <Button color="dark" size="medium" onClick={handleSave} disabled={isLoading || isSaving} className="px-[10px] h-10">
              {isSaving ? '저장 중...' : '저장'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
